import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataGrid,
  Icon,
  IconButton,
  Loading,
  Tooltip,
} from "../../../components";
import {
  history,
  OrdemServicoService,
  notification,
  PessoaService,
  OrdemServicosStatusPersonalizadosService,
} from "../../../services";
import { formatDate, formatPrice, responseErros } from "../../../utils";
import {
  FormFiltroAvancado,
  ImportarOrcamentoServicoDialog,
  ListStatusCustom,
} from "./components";
import { PermisoesHelper, value } from "../../../helpers";
import { ListPesquisaSatisfacaoOS } from "../add-ordem-servico/components";

const ordemServicoService = new OrdemServicoService();
const ordemServicosStatusPersonalizadosService =
  new OrdemServicosStatusPersonalizadosService();
const pessoaService = new PessoaService();

const ListOrdensServico = () => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("ordem-servicos-visualizar");
  const [loading, setLoading] = useState(false);
  const [ordensServico, setOrdensServico] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [colunasKanban, setColunasKanban] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [prestadoresList, setPrestadoresList] = useState([]);
  const [
    openDialogImportarOrcamentoServico,
    setOpenDialogImportarOrcamentoServico,
  ] = useState(false);

  const colunas = [
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      flex: 70,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const { value, row } = params;
        const isStatusPadrao =
          (value === "ABERTO" ||
            value === "FECHADO" ||
            value === "CANCELADO") &&
          !row.statusPersonalizadoId;
        const colunaKanban = isStatusPadrao
          ? colunasKanban.find((c) => c.status === value)
          : colunasKanban.find((c) => c.id === row.statusPersonalizadoId);
        return (
          <Tooltip title={colunaKanban?.status ?? value} arrow placement="right">
            <Icon>
              <i
                className="ph-fill ph-circle"
                style={{ color: colunaKanban?.cor ?? "#ccc", fontSize: 18 }}
              />
            </Icon>
          </Tooltip>
        );
      },
    },
    {
      field: "serieDocumento",
      headerName: "Série",
      align: "center",
      headerAlign: "center",
      width: 100,
      valueGetter: (params) => value.text(params.value),
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Documento",
      align: "center",
      headerAlign: "center",
      flex: 100,
      filtrar: true,
    },
    {
      field: "clienteNome",
      headerName: "Cliente",
      width: 300,
      sortable: false,
      valueGetter: (params) =>
        params.value ??
        listaPessoas.find((item) => item.id == params.row.clienteId)
          ?.nomeRazaoSocial,
    },
    {
      field: "dataAbertura",
      headerName: "Data Abertura",
      align: "center",
      headerAlign: "center",
      flex: 100,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "dataPrevistaFechamento",
      headerName: "Previsão Fechamento",
      align: "center",
      headerAlign: "center",
      flex: 150,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "totalOrdemServico",
      headerName: "Valor",
      align: "right",
      headerAlign: "right",
      flex: 150,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value, false),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 100,
      align: "center",
      headerAlign: "center",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div className="d-flex align-items-center">
            {params.row.pesquisas.length > 0 && (
              <ListPesquisaSatisfacaoOS
                listaOrdemServicoPesquisas={params.row.pesquisas}
              />
            )}
            {permissoesHelper.temPermisao("ordem-servicos-visualizar") && (
              <IconButton
                onClick={() => {
                  onClickEditOrdemServico(params.id);
                }}
              >
                <Tooltip title="Editar" arrow placement="right">
                  <i className="ph-fill ph-pencil" style={{ fontSize: 18 }}></i>
                </Tooltip>
              </IconButton>
            )}
            {permissoesHelper.temPermisao("ordem-servicos-cancelar") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickCancelarOrdemServico(params.id);
                  }}
                >
                  <Tooltip title="Cancelar" arrow placement="right">
                    <i
                      className="ph-fill ph-prohibit"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
            {permissoesHelper.temPermisao("ordem-servicos-excluir") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickDeleteOrdemServico(params.id);
                  }}
                >
                  <Tooltip title="Excluir" arrow placement="right">
                    <i
                      className="ph-fill ph-trash"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    buscarOrdensServico();
    buscarStatusPersonalizados();
    buscarPrestadoresServico();
  }, []);

  const buscarPrestadoresServico = () => {
    pessoaService
      .getAll({ nonPaginated: true, tiposPessoaId: 5 })
      .then((result) => {
        if (!result.isAxiosError) setPrestadoresList(result.data);
        else responseErros(result);
      });
  };

  const buscarOrdensServico = async (filtro) => {
    setLoading(true);
    const result = await ordemServicoService.getAll(filtro);
    if (!result.isAxiosError) {
      setOrdensServico(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarStatusPersonalizados = async () => {
    const result = await ordemServicosStatusPersonalizadosService.getAll();
    if (!result.isAxiosError) {
      const colunasCompletas = [
        { id: null, descricao: "ABERTO", cor: "#4FBB53" },
        ...(result.data || []).sort((a, b) => a.ordem - b.ordem),
        { id: null, descricao: "FECHADO", cor: "#004780" },
        { id: null, descricao: "CANCELADO", cor: "#DC3545" },
      ];
      setColunasKanban(
        colunasCompletas.map((col) => ({
          id: col.id,
          status: col.descricao,
          cor: col.cor,
          cards: [],
        })),
      );
    } else {
      responseErros(result);
    }
  };

  const buscarRelacionamentos = (listaDocumento) => {
    const idsPessoas = [
      ...new Set(
        listaDocumento
          .map(({ clienteId }) => (clienteId ? clienteId : 0))
          .filter((id, idx, arr) => arr.indexOf(id) === idx),
      ),
    ];
    if (idsPessoas.length) {
      pessoaService
        .getAll({ id: idsPessoas, nonPaginated: true })
        .then((result) => {
          if (!result.isAxiosError) setListaPessoas(result.data);
          else responseErros(result);
        });
    }
  };

  const onClickCadastrarOrdemServico = () => {
    history.push("/faturamento/ordem-servicos/create");
  };

  const onClickEditOrdemServico = (id) => {
    history.push(`/faturamento/ordem-servicos/${id}`);
  };

  const onClickCancelarOrdemServico = async (id) => {
    const retornoAlerta = await notification.confirmacao(
      "Cancelar!",
      "Tem certeza que deseja cancelar essa ordem de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await ordemServicoService.cancelar(id);
      if (!result.isAxiosError) {
        buscarOrdensServico();
        notification.alteracaoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const onClickDeleteOrdemServico = async (id) => {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      "Tem certeza que deseja excluir essa ordem de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await ordemServicoService.deletar(id);
      if (!result.isAxiosError) {
        buscarOrdensServico();
        notification.deletadoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const buscarPesquisaAvancada = async (filter) => {
    const porIntervalo = [];
    const filtrosAvancadosTemp = { ...filter };
    if (
      filtrosAvancadosTemp.dataAberturaStart &&
      filtrosAvancadosTemp.dataAberturaEnd
    ) {
      porIntervalo.push({
        coluna: "dataAbertura",
        de: filtrosAvancadosTemp.dataAberturaStart,
        ate: filtrosAvancadosTemp.dataAberturaEnd,
      });
    }
    if (
      filtrosAvancadosTemp.dataPrevistaFechamentoStart &&
      filtrosAvancadosTemp.dataPrevistaFechamentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataPrevistaFechamento",
        de: filtrosAvancadosTemp.dataPrevistaFechamentoStart,
        ate: filtrosAvancadosTemp.dataPrevistaFechamentoEnd,
      });
    }
    delete filtrosAvancadosTemp.dataAberturaStart;
    delete filtrosAvancadosTemp.dataAberturaEnd;
    delete filtrosAvancadosTemp.dataPrevistaFechamentoStart;
    delete filtrosAvancadosTemp.dataPrevistaFechamentoEnd;

    setLoading(true);
    const result = await ordemServicoService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrdensServico(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro === "simples") {
      buscarOrdensServico(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  return (
    <div className="d-flex flex-column">
      <Card>
        <CardHeader
          title="Ordens de Serviço"
          botaoVoltar
          action={
            <div className="d-flex gap-2 align-items-center px-2">
              {permissoesHelper.temPermisao("ordem-servicos-cadastrar") && (
                <Tooltip
                  title="Importar Orçamento de Serviço"
                  arrow
                  placement="bottom"
                >
                  <Button
                    onClick={() => setOpenDialogImportarOrcamentoServico(true)}
                    color="primary"
                    variant="outlined"
                  >
                    <i className="ph-fill ph-download-simple" />
                  </Button>
                </Tooltip>
              )}
              <Button
                onClick={onClickCadastrarOrdemServico}
                color="primary"
                bold
                variant="contained"
                disabled={
                  !permissoesHelper.temPermisao("ordem-servicos-cadastrar")
                }
              >
                Adicionar
              </Button>
            </div>
          }
        />
        <CardContent>
          <DataGrid
            rows={ordensServico}
            rowCount={rowCount}
            columns={colunas}
            onClickEdit={onClickEditOrdemServico}
            onClickDelete={onClickDeleteOrdemServico}
            paginationMode="server"
            visualizarShow={permissoesHelper.temPermisao(
              "ordem-servicos-visualizar",
            )}
            editShow={permissoesHelper.temPermisao("ordem-servicos-editar")}
            deleteShow={permissoesHelper.temPermisao("ordem-servicos-excluir")}
            sendServer={sendServerDatagrid}
            FormFiltroAvancado={FormFiltroAvancado}
            disableAcoes
            enableRowDoubleClick
            listaStatus={colunasKanban}
            ListStatusCustom={ListStatusCustom}
          />
        </CardContent>

        <ImportarOrcamentoServicoDialog
          open={openDialogImportarOrcamentoServico}
          setOpen={setOpenDialogImportarOrcamentoServico}
          prestadoresList={prestadoresList}
        />

        <Loading loading={loading} />
      </Card>
    </div>
  );
};

export default ListOrdensServico;
